import { useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { VehicleTrack } from "./Vehicles/VehicleTrack";
import moment from "moment";
import { loader } from "../../assets";
import { useWebSocket } from "../../hooks";
import { QIDragAndDropDetails } from "../../components";
import { useGetDevicesTrackingQuery, useGetVehiclesTrackingQuery } from "./services";
import { getLocalizedString } from "../../shared/translation";
import { ListDetails } from "./Shared/VehicleDetailsItems";
import { VehicleDetailsWindowView } from "./Vehicles/VehicleDetailsWindowView";
import { Dnd, useDndContainer, WindowTab } from "./Dnd";

export const TrackDevice = () => {
  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }
  const query = useQuery();
  const encodedText = query.get("q");

  const [decodedText, setDecodedText] = useState([]);
  const [trackMapRef, setTrackMapRef] = useState(null);
  const [getTracking, setGetTracking] = useState(false);
  const [selectedRange, setSelectedRange] = useState({});
  const [realTimeUpdatedData, setRealTimeUpdatedData] = useState({});
  const [deviceData, setDeviceData] = useState({});
  const [dockAlign, setDockAlign] = useState("split-down");
  const [maximizeMinimize, setmaximizeMinimize] = useState(false);
  const [showDetails, setShowDetails] = useState(true);
  const [showMapDetails, setShowMapDetails] = useState(false);
  const [validRequest, setValidRequest] = useState(null);
  const [deviceImei, setDeviceImei] = useState(null);
  const [plateNo, setPlateNo] = useState(null);
  const [token, setToken] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [driverName, setDriverName] = useState(null);
  const [orderNo, setOrderNo] = useState(null);

  // Decoding the encoded string received from query params
  useEffect(() => {
    try {
      const decoded = atob(encodedText);
      setDecodedText(decoded.split(";"));
    } catch (error) {
      setDecodedText("Invalid Base64 string");
    }
  }, [encodedText]);

  // If it is a valid request then set the access token
  useEffect(() => {
    if (validRequest !== "invalid" && validRequest !== null) {
      //Set the access token
      sessionStorage.setItem("fmdp-access-token", token);

      const timer = setTimeout(() => {
        setGetTracking(true);
      }, 1000); // 2 seconds delay

      return () => clearTimeout(timer); // Cleanup timeout on unmount
    }
  }, [token, validRequest]);

  // Set the data after decoding
  useEffect(() => {
    if (decodedText?.length > 0) {
      // Checking if start time and device imei or plate no and auth token is present
      if (
        decodedText[2] !== "" &&
        (decodedText[0] !== "" || decodedText[6] !== "") &&
        decodedText[1] !== ""
      ) {
        //Checking if start time is earlier than end time and difference b/w start and end time is less than equal to 7 days

        if (
          (decodedText[3] !== "" &&
            parseInt(decodedText[3]) > parseInt(decodedText[2]) &&
            moment(parseInt(decodedText[3])).diff(moment(parseInt(decodedText[2])), "days") <= 7) ||
          decodedText[3] === ""
        ) {
          // For either calling the vehicle or device api
          if (decodedText[0] !== "" && decodedText[6] !== "") {
            setValidRequest("both");
          } else if (decodedText[0] !== "") {
            setValidRequest("device");
          } else {
            setValidRequest("vehicle");
          }

          //Set the device imei
          setDeviceImei(decodedText[0]);

          //Set the access token
          setToken(decodedText[1]);

          //Set the start time
          setStartTime(decodedText[2]);

          //Set the end time
          setEndTime(decodedText[3]);

          //Set the order number
          setOrderNo(decodedText[4]);

          //Set the driver name
          setDriverName(decodedText[5]);

          //Set the plate number
          setPlateNo(decodedText[6]);

          // Set time range
          const startDate = moment(parseInt(decodedText[2]));
          const endDate =
            decodedText[3] === ""
              ? moment(startDate).add(1, "days")
              : moment(parseInt(decodedText[3]));

          //Set map with details
          if (decodedText[7] === "mapwithdetails") {
            setShowMapDetails(true);
          }

          setSelectedRange({ startDate, endDate });
        } else {
          setValidRequest("invalid");
        }
      } else {
        setValidRequest("invalid");
      }
    }
  }, [decodedText]);

  // Subscribe with device imei
  const { realTimedata } = useWebSocket("", "all", "sub_all", deviceImei);

  const getQueryParams = () => {
    if (validRequest === "both") {
      return {
        device_ids: [deviceImei],
        plate_number: [plateNo],
      };
    } else if (validRequest === "device") {
      return {
        device_ids: [deviceImei],
      };
    } else if (validRequest === "vehicle") {
      return {
        plate_numbers: [plateNo],
      };
    }
  };

  // Call the devices API with device imei and plate number if both are present else call only with device imei
  const { data: trackingDataDevice } = useGetDevicesTrackingQuery(getQueryParams(), {
    skip: validRequest === "device" || validRequest === "both" ? false : true,
  });

  // Call the vehicles API with plate number if only plate no is present
  const { data: trackingDataVehicle } = useGetVehiclesTrackingQuery(getQueryParams(), {
    skip: validRequest === "vehicle" ? false : true,
  });

  /* Set the real time data if the following conditions are met
   i) end date is not given - subscribe till 7 days from the start date
   ii) end date is given - subscribe if the end date is after current date */
  useEffect(() => {
    if (validRequest !== "invalid" && validRequest !== null) {
      if (
        (Object?.keys(realTimedata)?.length > 0 &&
          endTime === "" &&
          moment(moment()?.valueOf()).diff(moment(parseInt(startTime)), "days") <= 7) ||
        (endTime !== "" && endTime > realTimedata[startTime]?.source_time)
      ) {
        setRealTimeUpdatedData(realTimedata[deviceImei]);
      }
    }
  }, [realTimedata, validRequest, startTime, endTime]);

  // Set the device data if the tracking data is available
  useEffect(() => {
    if (trackingDataDevice?.latest_data?.length) {
      setDeviceData(trackingDataDevice?.latest_data[0]?.device_data);
    } else if (trackingDataVehicle?.latest_data?.length) {
      setDeviceData(trackingDataVehicle?.latest_data[0]?.device_data);
      setDeviceImei(trackingDataVehicle?.latest_data[0]?.device_data?.source_id);
    }
  }, [trackingDataDevice, trackingDataVehicle]);

  const HeaderDetails = () => {
    return (
      <>
        {(deviceData?.source_id || deviceImei) && (
          <li
            className="details-block_summary_list_item details-block_device-imei"
            title={deviceData?.source_id || deviceImei}
          >
            <strong>{getLocalizedString("device_imei", "Device IMEI")} :</strong>{" "}
            {deviceData?.source_id || deviceImei}
          </li>
        )}
        {(deviceData?.plate_number?.v || plateNo) && (
          <li
            className="details-block_summary_list_item details-block_plate-number"
            title={deviceData?.plate_number?.v || plateNo}
          >
            <strong>
              {getLocalizedString("plate_vehicle_plate_number", "Vehicle Plate Number")} :
            </strong>{" "}
            {deviceData?.plate_number?.v || plateNo}
          </li>
        )}
        {orderNo && (
          <li
            className="details-block_summary_list_item details-block_order-number"
            title={orderNo}
          >
            <strong>{getLocalizedString("order_no", "Order Number")} :</strong> {orderNo}
          </li>
        )}
        {driverName && (
          <li
            className="details-block_summary_list_item details-block_driver-name"
            title={driverName}
          >
            <strong>{getLocalizedString("driver_name", "Driver Name")} :</strong> {driverName}
          </li>
        )}
      </>
    );
  };

  const openDeviceModal = () => {
    setShowDetails(false);
    addNewBox(100);
  };

  const { activeBoxes, addNewBox, removeBox, revertBox, isItemsDragging, handleDragChange } =
    useDndContainer();

  const getMapTooltipDetails = () => {
    let details = "";
    if (!showMapDetails) {
      if (plateNo) {
        details += `<li>
                              <strong>Plate Number :</strong>
                              <span>${plateNo}</span>
                            </li>`;
      } else {
        details += `<li>
                              <strong>Device Imei :</strong>
                              <span>${deviceImei}</span>
                            </li>`;
      }

      details +=
        driverName &&
        `<li>
                            <strong>Driver Name :</strong>
                            <span>${driverName}</span>
                          </li>`;
      details +=
        orderNo &&
        `<li>
                            <strong>Order Number:</strong>
                            <span>${orderNo}</span>
                          </li>`;
    }
    return details;
  };

  return (
    <>
      {validRequest === null ? (
        <main className="login-layout authentication">
          <div className="login-form-container">
            <div>
              <h1 className="page-title">Loading...</h1>

              <img className="happenings" src={loader} />
            </div>
          </div>
        </main>
      ) : validRequest === "invalid" ? (
        <main className="login-layout authentication">
          <div className="login-form-container">
            <p className="login-form-container_error text-red-500 text-sm mb-4">Invalid Request!</p>
          </div>
        </main>
      ) : (
        <div className="tracking-only">
          <header className="qi-header">
            <span className="branding"></span>
            <h3 className="qi-header_title text-center">
              <strong className="qi-header_title_text">
                {plateNo !== ""
                  ? `Vehicle Plate Number : ${plateNo}`
                  : `Device Imei : ${deviceImei}`}
              </strong>
            </h3>
          </header>

          <main className="tracking-app-container-wrapper">
            <div className="tracking-app-main-container-wrapper">
              <main className="tracking-app-main-container">
                <article className="tracking-app-main-container_block LiveTrack">
                  <article
                    className={`map-and-details ${showMapDetails ? dockAlign : ""} ${
                      maximizeMinimize ? "minimized-details" : ""
                    }`}
                  >
                    <VehicleTrack
                      dockAlign={dockAlign}
                      deviceId={deviceImei}
                      realTimeData={realTimeUpdatedData}
                      setTrackMapRef={setTrackMapRef}
                      trackMapRef={trackMapRef}
                      // mapExpand={mapExpand}
                      selectedRange={selectedRange}
                      // setResetCount={setResetCount}
                      maximizeMinimize={maximizeMinimize}
                      mapProvider="qiMap"
                      showTooltip={true}
                      tooltip={getMapTooltipDetails()}

                      // mapboxAccessToken={mapboxAccessToken}
                      // googleMapsApiKey={googleMapsApiKey}
                    />
                    {showMapDetails && (
                      <article className={`details-block`}>
                        {showDetails && (
                          <QIDragAndDropDetails
                            deviceData={deviceData}
                            deviceId={deviceImei}
                            realTimedata={{
                              [deviceImei]: realTimeUpdatedData,
                            }}
                            currentTab="LiveTrack"
                            headerDetails={<HeaderDetails />}
                            dockAlign={dockAlign}
                            setDockAlign={setDockAlign}
                            maximizeMinimize={maximizeMinimize}
                            setmaximizeMinimize={setmaximizeMinimize}
                            setDetails={setShowDetails}
                            headerItems={[
                              "collapse",
                              "split-down",
                              "split-left",
                              "split-right",
                              "pop-out",
                            ]}
                            setPopup={openDeviceModal}
                            listDetails={
                              <ListDetails
                                // clientConfig={clientConfig}
                                realTimedata={{
                                  [deviceImei]: realTimeUpdatedData,
                                }}
                                deviceData={deviceData}
                                deviceId={deviceImei}
                              />
                            }
                          ></QIDragAndDropDetails>
                        )}
                      </article>
                    )}
                  </article>
                </article>
              </main>
              {showMapDetails && (
                <Dnd
                  layoutName="trackingLayout"
                  isItemsDragging={isItemsDragging}
                  handleDragChange={handleDragChange}
                  setDetails={setShowDetails}
                >
                  {Object.keys(activeBoxes).map((key) => (
                    <WindowTab
                      key={key}
                      id={key}
                      label={key}
                      {...activeBoxes[key].props}
                      // onClose={removeBox}
                      onMinimise={revertBox}
                      setShowDetails={setShowDetails}
                      setDockAlign={setDockAlign}
                      // setDeviceId={setDeviceId}
                      // setShowTrack={setShowTrack}
                      selectedVehiclePlate={deviceImei}
                      headerName={getLocalizedString("device_name", "Device Name")}
                      // setHighlight={setHighlight}
                      // removeLayerOnClose={removeLayerOnClose}
                      // removeRowSelection={removeRowSelection}
                    >
                      <VehicleDetailsWindowView
                        realTimedata={{
                          [deviceImei]: realTimeUpdatedData,
                        }}
                        deviceId={deviceImei}
                        deviceData={deviceData}
                        // clientConfig={clientConfig}
                        summaryDetails={<HeaderDetails />}
                        listDetails={
                          <ListDetails
                            // clientConfig={clientConfig}
                            realTimedata={{
                              [deviceImei]: realTimeUpdatedData,
                            }}
                            deviceData={deviceData}
                            deviceId={deviceImei}
                          />
                        }
                      />
                    </WindowTab>
                  ))}
                </Dnd>
              )}
            </div>
          </main>
        </div>
      )}
    </>
  );
};
